// ** React Imports
import { useState, useEffect, MouseEvent, useCallback, ReactElement } from 'react';

// ** MUI Imports
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import { DataGrid, GridEventListener } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import Icon from 'src/features/common/CustomIcon';
import Link from '@mui/material/Link';
// ** Store Imports
import { SelectOption } from 'src/types/common';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
// ** Custom Components Imports
import Tooltip from '@mui/material/Tooltip';
import { client } from 'src/utils/axios-client';
import { leadEndpoints } from 'src/configs/endpoints';

import _ from 'lodash';
import {
    ActivityType,
    CadenceListDialogStore,
    DashboardTodoStore,
    DashboardViewStore,
    InboundCallStore,
    Lead,
    LeadListStore,
    LeadScreenStore,
    QuickLeadDialogStore,
    ViewType,
} from '../../stores/LeadStore';
import {
    ActivityColor,
    colorDateTooltipOverdue,
    colorDateTooltipOverdueDashboard,
    nextDateDays,
    nextDateDaysDashboard,
} from '../../utils/date-utils';
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro';
import LeadDataFormater from '../leads/view/LeadDataFormater';
import IconButton from '@mui/material/IconButton';
import ErrorTwoToneIcon from '@mui/icons-material/ErrorTwoTone';
import TodayTwoToneIcon from '@mui/icons-material/TodayTwoTone';
import InsertInvitationTwoToneIcon from '@mui/icons-material/InsertInvitationTwoTone';
import DateRangeTwoToneIcon from '@mui/icons-material/DateRangeTwoTone';
import CalendarMonthTwoToneIcon from '@mui/icons-material/CalendarMonthTwoTone';
import { Divider } from '@mui/material';
import PhoneInTalkTwoToneIcon from '@mui/icons-material/PhoneInTalkTwoTone';
import ForwardToInboxTwoToneIcon from '@mui/icons-material/ForwardToInboxTwoTone';
import SmsTwoToneIcon from '@mui/icons-material/SmsTwoTone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsFailedTwoToneIcon from '@mui/icons-material/SmsFailedTwoTone';
import PhoneCallbackTwoToneIcon from '@mui/icons-material/PhoneCallbackTwoTone';
import MarkEmailReadTwoToneIcon from '@mui/icons-material/MarkEmailReadTwoTone';
import VoicemailTwoToneIcon from '@mui/icons-material/VoicemailTwoTone';
import { blue, green, grey } from '@mui/material/colors';
import StandardSkeleton from '../common/StandardSkeleton';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Avatar from '@mui/material/Avatar';
import AssignmentTurnedInTwoToneIcon from '@mui/icons-material/AssignmentTurnedInTwoTone';
import LocalFireDepartmentTwoTone from '@mui/icons-material/LocalFireDepartmentTwoTone';

type Props = {
    userId: string;
    title: string;
};

const DashboardTodoList = ({ userId, title }: Props) => {
    const todoRecord = DashboardTodoStore.useState((s) => s.record);
    const [reloadRecords, setReloadRecords] = useState<boolean>(false);
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [showActivityBreakdown, setShowActivityBreakdown] = useState<boolean>(false);

    useEffect(() => {
        loadRecords(userId);
    }, [reloadRecords]);

    const loadRecords = async (userId: string) => {
        setDataLoaded(false);
        const response = await client.get(leadEndpoints.getactivitytodoleads, { params: { userId } });
        DashboardTodoStore.update((s) => {
            s.record = response.data;
        });
        setDataLoaded(true);
    };

    const viewActivityWorkList = (activityDate: string, title: string, activityName: string) => {
        DashboardViewStore.update((s) => {
            s.activityDate = activityDate;
        });
        DashboardViewStore.update((s) => {
            s.activityName = activityName;
        });
        DashboardViewStore.update((s) => {
            s.showTodoList = false;
        });
        DashboardViewStore.update((s) => {
            s.showInboundCallList = false;
        });
        DashboardViewStore.update((s) => {
            s.showActivityWorkList = true;
        });
        DashboardViewStore.update((s) => {
            s.activityWorkListTitle = title;
        });
        /*DashboardViewStore.update(s => { s.pageNumber = 0 });*/
        DashboardViewStore.update((s) => {
            s.showDefault = false;
        });
        DashboardViewStore.update((s) => {
            s.showDetail = true;
        });
        DashboardViewStore.update((s) => {
            s.showTaskList = false;
        });
    };

    const viewInboundCallList = () => {
        DashboardViewStore.update((s) => {
            s.showTodoList = false;
        });
        DashboardViewStore.update((s) => {
            s.showActivityWorkList = false;
        });
        DashboardViewStore.update((s) => {
            s.showInboundCallList = true;
        });
        DashboardViewStore.update((s) => {
            s.showDefault = false;
        });
        DashboardViewStore.update((s) => {
            s.showDetail = true;
        });
        DashboardViewStore.update((s) => {
            s.showTaskList = false;
        });
    };

    const viewTaskList = () => {
        DashboardViewStore.update((s) => {
            s.showTodoList = false;
        });
        DashboardViewStore.update((s) => {
            s.showActivityWorkList = false;
        });
        DashboardViewStore.update((s) => {
            s.showInboundCallList = false;
        });
        DashboardViewStore.update((s) => {
            s.showTaskList = true;
        });
        DashboardViewStore.update((s) => {
            s.showDefault = false;
        });
        DashboardViewStore.update((s) => {
            s.showDetail = true;
        });
    };

    useEffect(() => {
        setActivityBreakdown();
    }, [dataLoaded]);

    const setActivityBreakdown = () => {
        setShowActivityBreakdown(false);
        if (
            todoRecord.outboundPhoneCallLeadCount > 0 ||
            todoRecord.outboundEmailLeadCount > 0 ||
            todoRecord.discoveryCallCount > 0 ||
            todoRecord.outboundSMSLeadCount > 0 ||
            todoRecord.outboundSocialMediaLeadCount > 0 ||
            todoRecord.inboundPhoneCallLeadCount > 0 ||
            todoRecord.inboundEmailLeadCount > 0 ||
            todoRecord.inboundSocialMediaLeadCount > 0 ||
            todoRecord.inboundSMSLeadCount > 0 ||
            todoRecord.incomingVoicemails > 0 ||
            todoRecord.outboundWhatsAppLeadCount > 0
        ) {
            setShowActivityBreakdown(true);
        }
    };

    if (dataLoaded) {
        return (
            <Box>
                <Card sx={{ display: 'flex', ml: 3.0 }}>
                    <Grid item xs={12} md={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Card>
                                <Typography sx={{ fontSize: 16, color: 'DarkGrey', ml: 3 }}>{title}</Typography>
                            </Card>
                        </Box>
                    </Grid>
                </Card>
                <Card sx={{ display: 'flex', ml: 3.0, mt: 2.0, pb: 10 }}>
                    <Grid container spacing={2} justifyContent={'left'} sx={{ ml: 3.0, mt: 2, mb: 2 }}>
                        {todoRecord.overdueLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <ErrorTwoToneIcon color="error" />
                                    &nbsp;
                                    <Tooltip title="Overdue and today">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'overdueall',
                                                    'Activities that require actioning',
                                                    '',
                                                )
                                            }
                                        >
                                            Require action ({todoRecord.overdueLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.todayLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <TodayTwoToneIcon color="success" />
                                    &nbsp;
                                    <Tooltip title="Today">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() => viewActivityWorkList('today', 'Activities due today', '')}
                                        >
                                            Today ({todoRecord.todayLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.tomorrowLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <InsertInvitationTwoToneIcon color="info" />
                                    &nbsp;
                                    <Tooltip title="Tomorrow">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'tomorrow',
                                                    'Activities scheduled for tomorrow',
                                                    '',
                                                )
                                            }
                                        >
                                            Tomorrow ({todoRecord.tomorrowLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.thisWeekLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <DateRangeTwoToneIcon color="action" />
                                    &nbsp;
                                    <Tooltip title="This week">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'thisweek',
                                                    'Activities scheduled for this week',
                                                    '',
                                                )
                                            }
                                        >
                                            This week ({todoRecord.thisWeekLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.thisMonthLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <CalendarMonthTwoToneIcon color="action" />
                                    &nbsp;
                                    <Tooltip title="This month">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'thismonth',
                                                    'Activities scheduled for this month',
                                                    '',
                                                )
                                            }
                                        >
                                            This month ({todoRecord.thisMonthLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {showActivityBreakdown && (
                            <Box>
                                <Grid item xs={12} md={12}>
                                    <Divider />
                                </Grid>

                                <Grid item xs={12} md={12}>
                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: 16, color: 'CornflowerBlue' }} gutterBottom>
                                            Today's Activities (including overdue)
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Box>
                        )}
                        {todoRecord.discoveryCallCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <WhatsAppIcon color="warning" />
                                    &nbsp;
                                    <Tooltip title="Discovery Calls">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'overdueall',
                                                    "Today's discovery calls",
                                                    'Discovery Call',
                                                )
                                            }
                                        >
                                            Discovery calls ({todoRecord.discoveryCallCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.outboundPhoneCallLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <PhoneInTalkTwoToneIcon color="warning" />
                                    &nbsp;
                                    <Tooltip title="Outgoing Phone Calls">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'overdueall',
                                                    "Today's outgoing phone calls",
                                                    'Outbound Phone Call',
                                                )
                                            }
                                        >
                                            Outgoing phone calls ({todoRecord.outboundPhoneCallLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.outboundEmailLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <ForwardToInboxTwoToneIcon color="warning" />
                                    &nbsp;
                                    <Tooltip title="Outgoing Emails">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'overdueall',
                                                    "Today's outgoing emails",
                                                    'Outbound Email',
                                                )
                                            }
                                        >
                                            Outgoing emails ({todoRecord.outboundEmailLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.outboundSocialMediaLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <ForwardToInboxTwoToneIcon color="warning" />
                                    &nbsp;
                                    <Tooltip title="Outgoing Social Media">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'overdueall',
                                                    "Today's outgoing social media messages",
                                                    'Outbound Social Media',
                                                )
                                            }
                                        >
                                            Outgoing social media ({todoRecord.outboundSocialMediaLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.outboundSMSLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <SmsTwoToneIcon color="warning" />
                                    &nbsp;
                                    <Tooltip title="Outgoing SMS">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'overdueall',
                                                    "Today's outgoing SMS Messages",
                                                    'Outbound SMS',
                                                )
                                            }
                                        >
                                            Outgoing SMS ({todoRecord.outboundSMSLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.outboundWhatsAppLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <WhatsAppIcon color="warning" />
                                    &nbsp;
                                    <Tooltip title="Outgoing WhatsApp">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'overdueall',
                                                    "Today's outgoing WhatsApp Messages",
                                                    'Outbound WhatsApp',
                                                )
                                            }
                                        >
                                            Outgoing WhatsApp ({todoRecord.outboundWhatsAppLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.inboundPhoneCallLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <PhoneCallbackTwoToneIcon color="warning" />
                                    &nbsp;
                                    <Tooltip title="Incoming Phone Calls">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'overdueall',
                                                    "Today's incoming phone calls",
                                                    'Inbound Phone Call',
                                                )
                                            }
                                        >
                                            Incoming phone calls ({todoRecord.inboundPhoneCallLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.inboundEmailLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <MarkEmailReadTwoToneIcon color="warning" />
                                    &nbsp;
                                    <Tooltip title="Incoming Emails">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'overdueall',
                                                    "Today's incoming emails",
                                                    'Inbound Email',
                                                )
                                            }
                                        >
                                            Incoming emails ({todoRecord.inboundEmailLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.inboundSocialMediaLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <SmsTwoToneIcon color="warning" />
                                    &nbsp;
                                    <Tooltip title="Incoming social media">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'overdueall',
                                                    "Today's incoming social media messages",
                                                    'Inbound SMS',
                                                )
                                            }
                                        >
                                            Outgoing SMS ({todoRecord.inboundSocialMediaLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.inboundSMSLeadCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <SmsFailedTwoToneIcon color="warning" />
                                    &nbsp;
                                    <Tooltip title="Incoming SMS Messages">
                                        <Link
                                            underline="hover"
                                            component="button"
                                            variant="body2"
                                            onClick={() =>
                                                viewActivityWorkList(
                                                    'overdueall',
                                                    "Today's incoming SMS messages",
                                                    'Inbound SMS',
                                                )
                                            }
                                        >
                                            Incoming SMS ({todoRecord.inboundSMSLeadCount})
                                        </Link>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        )}
                        {todoRecord.openTasksCount > 0 && (
                            <Grid item xs={12} md={12}>
                                <Divider />
                            </Grid>
                        )}

                        <Box>
                            <Grid item xs={12} md={12}>
                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: 16, color: 'CornflowerBlue' }} gutterBottom>
                                        Other
                                    </Typography>
                                </Box>
                            </Grid>
                            {todoRecord.hotLeadCount > 0 && (
                                <Grid item xs={12} md={12}>
                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                        <LocalFireDepartmentTwoTone sx={{ color: 'red' }} />
                                        &nbsp;
                                        <Tooltip title="Hot Leads">
                                            <Link
                                                underline="hover"
                                                component="button"
                                                variant="body2"
                                                onClick={() => viewActivityWorkList('hot', 'Hot Leads', '')}
                                            >
                                                Hot Leads ({todoRecord.hotLeadCount})
                                            </Link>
                                        </Tooltip>
                                    </Box>
                                </Grid>
                            )}
                            {todoRecord.openTasksCount > 0 && (
                                <Grid item xs={12} md={12}>
                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                        <AssignmentTurnedInTwoToneIcon color="warning" />
                                        &nbsp;
                                        <Tooltip title="Missed Calls">
                                            <Link
                                                underline="hover"
                                                component="button"
                                                variant="body2"
                                                onClick={() => viewTaskList()}
                                            >
                                                Open Tasks ({todoRecord.openTasksCount})
                                            </Link>
                                        </Tooltip>
                                    </Box>
                                </Grid>
                            )}
                            {todoRecord.incomingVoicemails > 0 && (
                                <Grid item xs={12} md={12}>
                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                        <VoicemailTwoToneIcon color="warning" />
                                        &nbsp;
                                        <Tooltip title="Missed Calls">
                                            <Link
                                                underline="hover"
                                                component="button"
                                                variant="body2"
                                                onClick={() => viewInboundCallList()}
                                            >
                                                Missed Calls ({todoRecord.incomingVoicemails})
                                            </Link>
                                        </Tooltip>
                                    </Box>
                                </Grid>
                            )}
                        </Box>
                    </Grid>
                </Card>
            </Box>
        );
    } else {
        return (
            <Box>
                <Card variant="outlined">
                    <CardContent>
                        <Grid container spacing={1} justifyContent={'left'}>
                            <Grid item xs={12} md={1} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                                <Skeleton variant="circular">
                                    <Avatar />
                                </Skeleton>
                            </Grid>
                            <Grid item xs={12} md={11} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                                <Skeleton animation="wave" height={20} width="30%" />
                            </Grid>
                            <Grid item xs={12} md={1} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                                <Skeleton variant="circular">
                                    <Avatar />
                                </Skeleton>
                            </Grid>
                            <Grid item xs={12} md={11} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                                <Skeleton animation="wave" height={20} width="30%" />
                            </Grid>
                            <Grid item xs={12} md={1} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                                <Skeleton variant="circular">
                                    <Avatar />
                                </Skeleton>
                            </Grid>
                            <Grid item xs={12} md={11} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                                <Skeleton animation="wave" height={20} width="30%" />
                            </Grid>
                            <Grid item xs={12} md={1} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                                <Skeleton variant="circular">
                                    <Avatar />
                                </Skeleton>
                            </Grid>
                            <Grid item xs={12} md={11} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                                <Skeleton animation="wave" height={20} width="30%" />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        );
    }
};

export { DashboardTodoList };
export default DashboardTodoList;
